import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { ASSET_STATE_COUNTS_QUERY_KEY } from '@/hooks/api/assets/useGetAssetStateCounts';
import { ASSET_FILE_TYPE_COUNTS_QUERY_KEY } from '@/hooks/api/assets/useGetAssetFileTypeCounts';
import { LABELING_COVERAGE_QUERY_KEY } from '@/hooks/api/assets/useGetLabelingCoverage';
import { METADATA_COVERAGE_QUERY_KEY } from '@/hooks/api/assets/useGetMetadataCoverage';
import { RECENT_FAILURES_QUERY_KEY } from '@/hooks/api/assets/useGetRecentFailures';
import { RECENT_PART_FINDER_ACTIVITY_QUERY_KEY } from '@/hooks/api/part-finder-reports/useGetRecentPartFinderActivity';
import { LATEST_DUPLICATION_REPORT_QUERY_KEY } from '@/hooks/api/reports/useGetLatestDuplicationReport';
import { RECENT_REPORTS_QUERY_KEY } from '@/hooks/api/reports/useGetRecentReports';
import { TENANT_ACTIVITY_METRICS_QUERY_KEY } from '@/hooks/api/tenants/useGetTenantActivityMetrics';
import { DASHBOARD_VERSION_POLL_INTERVAL_MS } from '@/hooks/useDataPolling/pollingConstants';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';

export const DASHBOARD_VERSION_QUERY_KEY = 'dashboardVersion';

const PANEL_QUERY_KEYS = [
  ASSET_STATE_COUNTS_QUERY_KEY,
  ASSET_FILE_TYPE_COUNTS_QUERY_KEY,
  METADATA_COVERAGE_QUERY_KEY,
  LABELING_COVERAGE_QUERY_KEY,
  RECENT_PART_FINDER_ACTIVITY_QUERY_KEY,
  RECENT_FAILURES_QUERY_KEY,
  RECENT_REPORTS_QUERY_KEY,
  LATEST_DUPLICATION_REPORT_QUERY_KEY,
  TENANT_ACTIVITY_METRICS_QUERY_KEY,
];

/**
 * Polls one cheap token instead of re-running every panel's aggregation on a timer, and refetches
 * the panels only when it moves. Switching it off leaves whatever is on screen alone.
 */
export function useDashboardLiveRefresh({ enabled = true }: { enabled?: boolean } = {}) {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const queryClient = useQueryClient();

  const { data: version } = useQuery({
    queryKey: [DASHBOARD_VERSION_QUERY_KEY, { tenantId }],
    queryFn: async () => api.tenantDashboard.getDashboardVersion(tenantId),
    select: response => response.data.version,
    enabled: !!tenantId && enabled,
    refetchInterval: enabled ? DASHBOARD_VERSION_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: 'always',
    refetchOnMount: 'always',
  });

  const seenVersion = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!version) return;
    const isFirstRead = seenVersion.current === undefined;
    const changed = seenVersion.current !== version;
    seenVersion.current = version;
    if (isFirstRead || !changed) return;

    for (const key of PANEL_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [version, queryClient]);
}
